import { createHttpClient, type AuthHeaderProvider, type HttpClient } from "./http";
import { retailersResource, usersResource, visitsResource } from "./resources/identity";
import { skusResource } from "./resources/catalog";
import { ordersResource } from "./resources/order";
import { cartsResource } from "./resources/cart";
import { inventoryResource } from "./resources/inventory";
import { routesResource } from "./resources/dispatch";
import { settlementsResource } from "./resources/settlement";
import { whatsappResource } from "./resources/whatsapp";
import { analyticsResource } from "./resources/analytics";
import { vendorsResource } from "./resources/vendor";
import { sampleRequestsResource } from "./resources/sampleRequest";
import { focusPackagesResource } from "./resources/focusPackage";
import { competitorPricesResource } from "./resources/competitorPrice";
import { superAdminResource } from "./resources/superAdmin";

export * from "./types";
export { ApiError } from "./http";
export type { AuthHeaderProvider, HttpClient } from "./http";

export interface MyDailyDirectClientConfig {
  /** Base URL for the API. Defaults to same-origin "/api/trado/api/v1". */
  baseUrl?: string;
  getAuthHeaders: AuthHeaderProvider;
}

export type TradoClientConfig = MyDailyDirectClientConfig;

export interface MyDailyDirectClient {
  http: HttpClient;
  retailers: ReturnType<typeof retailersResource>;
  users: ReturnType<typeof usersResource>;
  visits: ReturnType<typeof visitsResource>;
  skus: ReturnType<typeof skusResource>;
  orders: ReturnType<typeof ordersResource>;
  carts: ReturnType<typeof cartsResource>;
  inventory: ReturnType<typeof inventoryResource>;
  routes: ReturnType<typeof routesResource>;
  settlements: ReturnType<typeof settlementsResource>;
  whatsapp: ReturnType<typeof whatsappResource>;
  analytics: ReturnType<typeof analyticsResource>;
  vendors: ReturnType<typeof vendorsResource>;
  sampleRequests: ReturnType<typeof sampleRequestsResource>;
  focusPackages: ReturnType<typeof focusPackagesResource>;
  competitorPrices: ReturnType<typeof competitorPricesResource>;
  superAdmin: ReturnType<typeof superAdminResource>;
}

export type TradoClient = MyDailyDirectClient;

export const DEFAULT_BASE_URL = "/api/trado/api/v1";

/** Creates a fully typed MyDailyDirect API client bound to a per-request auth-header provider. */
export function createMyDailyDirectClient(config: MyDailyDirectClientConfig): MyDailyDirectClient {
  const http = createHttpClient({
    baseUrl: config.baseUrl ?? DEFAULT_BASE_URL,
    getAuthHeaders: config.getAuthHeaders,
  });
  return {
    http,
    retailers: retailersResource(http),
    users: usersResource(http),
    visits: visitsResource(http),
    skus: skusResource(http),
    orders: ordersResource(http),
    carts: cartsResource(http),
    inventory: inventoryResource(http),
    routes: routesResource(http),
    settlements: settlementsResource(http),
    whatsapp: whatsappResource(http),
    analytics: analyticsResource(http),
    vendors: vendorsResource(http),
    sampleRequests: sampleRequestsResource(http),
    focusPackages: focusPackagesResource(http),
    competitorPrices: competitorPricesResource(http),
    superAdmin: superAdminResource(http),
  };
}

export const createTradoClient = createMyDailyDirectClient;
